import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, Download, GitCompare, Save, Share2, Copy } from "lucide-react";

/** Iter 172 · Multi-brand side-by-side comparison (up to 6 brands). */

const STATUS_TONE = {
  active: "border-emerald-500/40 text-emerald-700 bg-emerald-50",
  paused: "border-amber-500/40 text-amber-700 bg-amber-50",
  archived: "border-border text-muted-foreground bg-secondary/40",
};

const HEALTH_TONE = {
  excellent: "text-emerald-700 bg-emerald-50 border-emerald-300",
  good: "text-sky-700 bg-sky-50 border-sky-300",
  needs_work: "text-amber-700 bg-amber-50 border-amber-300",
  critical: "text-rose-700 bg-rose-50 border-rose-300",
};

function Row({ label, values, testid, isText, isList, isMulti }) {
  return (
    <tr className="border-t border-border align-top" data-testid={testid}>
      <th className="py-2 px-3 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold w-40 bg-muted/30 text-left sticky left-0">
        {label}
      </th>
      {values.map((v, i) => (
        <td key={i} className="py-2 px-3 text-xs align-top">
          {isList ? (
            <ListCell items={v} />
          ) : isMulti ? (
            <MultiCell blocks={v} />
          ) : isText ? (
            <div className="text-xs whitespace-pre-wrap max-w-[260px]">{v || <span className="text-muted-foreground">—</span>}</div>
          ) : (
            <div className="max-w-[260px]">{v || <span className="text-muted-foreground">—</span>}</div>
          )}
        </td>
      ))}
    </tr>
  );
}

function ListCell({ items }) {
  if (!Array.isArray(items) || items.length === 0)
    return <span className="text-muted-foreground">—</span>;
  return (
    <ul className="text-xs space-y-0.5 max-w-[260px]">
      {items.slice(0, 8).map((it, i) => (
        <li key={i} className="truncate">
          • {typeof it === "string" ? it : (it.title || it.name || it.label || JSON.stringify(it))}
        </li>
      ))}
      {items.length > 8 && <li className="text-muted-foreground">+{items.length - 8} more…</li>}
    </ul>
  );
}

function MultiCell({ blocks }) {
  if (!blocks || Object.keys(blocks).length === 0)
    return <span className="text-muted-foreground">—</span>;
  return (
    <div className="text-xs space-y-1 max-w-[260px]">
      {Object.entries(blocks).map(([k, v]) => (
        <div key={k}>
          <span className="text-[9px] uppercase tracking-widest text-muted-foreground">{k}</span>
          <div className="text-xs">{Array.isArray(v)
            ? (v.length ? v.slice(0, 6).map((x, i) =>
                <div key={i}>• {typeof x === "string" ? x : (x.title || x.name || JSON.stringify(x))}</div>)
              : "—")
            : (v || "—")}</div>
        </div>
      ))}
    </div>
  );
}

function csvEscape(v) {
  const s = v === null || v === undefined ? "" : String(v);
  return `"${s.replace(/"/g, '""').replace(/\n/g, " ")}"`;
}

function stringifyForCsv(v) {
  if (v === null || v === undefined) return "";
  if (Array.isArray(v)) {
    return v.map((x) => typeof x === "string" ? x : (x.title || x.name || JSON.stringify(x))).join("; ");
  }
  if (typeof v === "object") return JSON.stringify(v);
  return v;
}

export default function BrandCompareDialog({ wsId, ids, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [showSave, setShowSave] = useState(false);
  const [snapName, setSnapName] = useState("");
  const [snapNotes, setSnapNotes] = useState("");
  const [snapShareable, setSnapShareable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedSnap, setSavedSnap] = useState(null);

  useEffect(() => {
    setBusy(true);
    api.get(`/workspaces/${wsId}/brands/compare`, { params: { ids: ids.join(",") } })
       .then((r) => setData(r.data))
       .catch((e) => { toast.error(apiErrorMessage(e, "Compare failed")); onClose?.(); })
       .finally(() => setBusy(false));
  }, [wsId, ids, onClose]);

  const brands = data?.brands || [];

  const saveSnapshot = async () => {
    if (!snapName.trim()) { toast.error("Give the snapshot a name"); return; }
    setSaving(true);
    try {
      const r = await api.post(
        `/workspaces/${wsId}/brand-compare/snapshots`,
        { name: snapName.trim(), brand_ids: ids, notes: snapNotes,
          shareable: snapShareable });
      setSavedSnap(r.data);
      toast.success("Snapshot saved");
      if (!snapShareable) {
        setShowSave(false);
        setSnapName(""); setSnapNotes("");
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const shareUrl = savedSnap?.share_token
    ? `${window.location.origin}/compare/${savedSnap.share_token}`
    : null;
  const copyShareUrl = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied");
    } catch { toast.error("Could not copy"); }
  };

  const rows = useMemo(() => {
    if (brands.length === 0) return [];
    const val = (fn) => brands.map(fn);
    return [
      { label: "Client", testid: "cmp-row-client",
        values: val((b) => b.client_name || "—") },
      { label: "Industry", testid: "cmp-row-industry",
        values: val((b) => [b.industry, b.sub_industry].filter(Boolean).join(" · ") || "—") },
      { label: "Website", testid: "cmp-row-website",
        values: val((b) => b.website
          ? <a href={b.website} target="_blank" rel="noreferrer" className="text-primary hover:underline">{b.website.replace(/^https?:\/\//, "").slice(0, 40)}</a>
          : "—") },
      { label: "Tagline", testid: "cmp-row-tagline", isText: true,
        values: val((b) => (b.core || {}).tagline) },
      { label: "Mission", testid: "cmp-row-mission", isText: true,
        values: val((b) => (b.core || {}).mission) },
      { label: "Vision", testid: "cmp-row-vision", isText: true,
        values: val((b) => (b.core || {}).vision) },
      { label: "Positioning", testid: "cmp-row-positioning", isText: true,
        values: val((b) => (b.core || {}).positioning) },
      { label: "Values", testid: "cmp-row-values", isList: true,
        values: val((b) => (b.core || {}).values || []) },
      { label: "Objectives", testid: "cmp-row-objectives", isList: true,
        values: val((b) => (b.strategy || {}).objectives || []) },
      { label: "Target market", testid: "cmp-row-target-market", isText: true,
        values: val((b) => (b.strategy || {}).target_market) },
      { label: "Personas", testid: "cmp-row-personas", isList: true,
        values: val((b) => (b.strategy || {}).personas || []) },
      { label: "USPs", testid: "cmp-row-usps", isList: true,
        values: val((b) => b.usps || []) },
      { label: "SWOT", testid: "cmp-row-swot", isMulti: true,
        values: val((b) => b.swot || {}) },
      { label: "Voice attributes", testid: "cmp-row-voice-attr", isList: true,
        values: val((b) => (b.voice || {}).attributes || []) },
      { label: "Voice do's", testid: "cmp-row-voice-dos", isList: true,
        values: val((b) => (b.voice || {}).dos || []) },
      { label: "Voice don'ts", testid: "cmp-row-voice-donts", isList: true,
        values: val((b) => (b.voice || {}).donts || []) },
      { label: "Colors", testid: "cmp-row-colors", isList: true,
        values: val((b) => (b.visual || {}).colors || []) },
      { label: "Typography", testid: "cmp-row-typography", isList: true,
        values: val((b) => (b.visual || {}).typography || []) },
      { label: "Socials", testid: "cmp-row-socials", isList: true,
        values: val((b) => (b.digital || {}).socials || []) },
      { label: "SEO keywords", testid: "cmp-row-seo", isList: true,
        values: val((b) => (b.digital || {}).seo_keywords || []) },
      { label: "Pillars", testid: "cmp-row-pillars", isList: true,
        values: val((b) => (b.pillars || [])
          .map((p) => `${p.name || ""} (${p.target_pct || 0}%)`)) },
      { label: "Competitors", testid: "cmp-row-competitors", isList: true,
        values: val((b) => b.competitors || []) },
      { label: "Ops SLAs", testid: "cmp-row-slas", isText: true,
        values: val((b) => (b.ops || {}).slas) },
      { label: "Timezone", testid: "cmp-row-timezone",
        values: val((b) => (b.ops || {}).timezone || "—") },
      { label: "Important dates", testid: "cmp-row-important-dates", isList: true,
        values: val((b) => (b.important_dates || []).map((d) =>
          `${d.title || d.name || ""} ${d.date || ""}`)) },
      { label: "Projects", testid: "cmp-row-projects",
        values: val((b) => `${b.projects_summary?.active || 0} active · ${b.projects_summary?.total || 0} total`) },
      { label: "Packages", testid: "cmp-row-packages",
        values: val((b) => `${b.packages_summary?.total || 0} total`) },
    ];
  }, [brands]);

  const downloadCsv = () => {
    if (brands.length === 0) return;
    const header = ["Field", ...brands.map((b) => b.name)];
    const lines = [header.map(csvEscape).join(",")];
    const flatRows = [
      ["ID", (b) => b.id],
      ["Status", (b) => b.status],
      ["Client", (b) => b.client_name],
      ["Industry", (b) => b.industry],
      ["Sub-industry", (b) => b.sub_industry],
      ["Website", (b) => b.website],
      ["Health score", (b) => `${b.health_score} (${b.health_band})`],
      ["Tagline", (b) => (b.core || {}).tagline],
      ["Mission", (b) => (b.core || {}).mission],
      ["Vision", (b) => (b.core || {}).vision],
      ["Positioning", (b) => (b.core || {}).positioning],
      ["Values", (b) => stringifyForCsv((b.core || {}).values)],
      ["Objectives", (b) => stringifyForCsv((b.strategy || {}).objectives)],
      ["Target market", (b) => (b.strategy || {}).target_market],
      ["Personas", (b) => stringifyForCsv((b.strategy || {}).personas)],
      ["USPs", (b) => stringifyForCsv(b.usps)],
      ["SWOT · strengths", (b) => stringifyForCsv((b.swot || {}).strengths)],
      ["SWOT · weaknesses", (b) => stringifyForCsv((b.swot || {}).weaknesses)],
      ["SWOT · opportunities", (b) => stringifyForCsv((b.swot || {}).opportunities)],
      ["SWOT · threats", (b) => stringifyForCsv((b.swot || {}).threats)],
      ["Voice attributes", (b) => stringifyForCsv((b.voice || {}).attributes)],
      ["Voice do's", (b) => stringifyForCsv((b.voice || {}).dos)],
      ["Voice don'ts", (b) => stringifyForCsv((b.voice || {}).donts)],
      ["Colors", (b) => stringifyForCsv((b.visual || {}).colors)],
      ["Typography", (b) => stringifyForCsv((b.visual || {}).typography)],
      ["Socials", (b) => stringifyForCsv((b.digital || {}).socials)],
      ["SEO keywords", (b) => stringifyForCsv((b.digital || {}).seo_keywords)],
      ["Pillars", (b) => stringifyForCsv(b.pillars)],
      ["Competitors", (b) => stringifyForCsv(b.competitors)],
      ["Ops SLAs", (b) => (b.ops || {}).slas],
      ["Timezone", (b) => (b.ops || {}).timezone],
      ["Important dates", (b) => stringifyForCsv(b.important_dates)],
      ["Projects active", (b) => b.projects_summary?.active || 0],
      ["Projects total", (b) => b.projects_summary?.total || 0],
      ["Packages total", (b) => b.packages_summary?.total || 0],
    ];
    flatRows.forEach(([lab, fn]) => {
      lines.push([lab, ...brands.map((b) => stringifyForCsv(fn(b)))].map(csvEscape).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `brand-comparison-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-[96vw] w-[96vw] max-h-[92vh] overflow-hidden flex flex-col p-0"
                       data-testid="brand-compare-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-border">
          <div className="flex items-center gap-3">
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="w-5 h-5" /> Brand Comparison
              <span className="text-sm text-muted-foreground font-normal">
                ({brands.length} brand{brands.length === 1 ? "" : "s"})
              </span>
            </DialogTitle>
            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" className="rounded-none h-8" onClick={() => setShowSave((s) => !s)}
                        data-testid="brand-compare-save-btn">
                <Save className="w-3.5 h-3.5 mr-1.5" /> Save snapshot
              </Button>
              <Button variant="outline" className="rounded-none h-8" onClick={downloadCsv}
                        disabled={busy || brands.length === 0}
                        data-testid="brand-compare-csv-btn">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
              </Button>
              <Button variant="ghost" className="rounded-none h-8 w-8 p-0" onClick={onClose}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        {showSave && (
          <div className="border-b border-border px-6 py-3 bg-muted/30 space-y-2"
                 data-testid="brand-compare-save-panel">
            <div className="flex items-center gap-2">
              <input value={snapName} onChange={(e) => setSnapName(e.target.value)}
                       placeholder="Snapshot name (e.g. Q1 2026 · Apparel comp set)"
                       className="h-9 flex-1 border border-border px-2 text-sm rounded-none bg-background"
                       data-testid="brand-compare-save-name" />
              <label className="text-xs flex items-center gap-1.5">
                <input type="checkbox" checked={snapShareable}
                         onChange={(e) => setSnapShareable(e.target.checked)}
                         data-testid="brand-compare-save-shareable" />
                Shareable link
              </label>
              <Button className="rounded-none h-9" onClick={saveSnapshot} disabled={saving}
                        data-testid="brand-compare-save-submit">
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
            <textarea value={snapNotes} onChange={(e) => setSnapNotes(e.target.value)}
                        rows={2} placeholder="Optional notes / pitch context…"
                        className="w-full text-xs p-2 border border-border rounded-none bg-background"
                        data-testid="brand-compare-save-notes" />
            {savedSnap?.share_token && (
              <div className="border border-primary/40 bg-primary/5 p-2 flex items-center gap-2"
                     data-testid="brand-compare-share-block">
                <Share2 className="w-3.5 h-3.5 text-primary" />
                <span className="text-[10px] uppercase tracking-widest text-primary font-semibold">Share link</span>
                <input value={shareUrl} readOnly
                         className="flex-1 h-7 border border-border px-2 text-xs rounded-none bg-background font-mono"
                         data-testid="brand-compare-share-url" />
                <Button size="sm" variant="outline" className="rounded-none h-7"
                          onClick={copyShareUrl} data-testid="brand-compare-share-copy">
                  <Copy className="w-3 h-3 mr-1" /> Copy
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="flex-1 overflow-auto" data-testid="brand-compare-scroll">
          {busy ? (
            <div className="p-12 text-center text-muted-foreground">Loading comparison…</div>
          ) : brands.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">No brands to compare.</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-background z-10 border-b border-border">
                <tr>
                  <th className="py-3 px-3 text-left w-40 bg-muted/40"></th>
                  {brands.map((b) => (
                    <th key={b.id} className="py-3 px-3 text-left align-top border-l border-border min-w-[240px]"
                          data-testid={`brand-compare-col-${b.id}`}>
                      <div className="flex items-center gap-2">
                        {b.logo_url
                          ? <img src={b.logo_url} alt="" className="w-8 h-8 object-contain border border-border bg-white" />
                          : <div className="w-8 h-8 border border-border bg-muted/40" />}
                        <div className="min-w-0">
                          <div className="font-semibold truncate">{b.name}</div>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className={`text-[9px] uppercase tracking-wider border px-1 py-0.5 ${STATUS_TONE[b.status] || ""}`}>
                              {b.status || "—"}
                            </span>
                            <span className={`text-[9px] uppercase tracking-wider border px-1 py-0.5 ${HEALTH_TONE[b.health_band] || ""}`}>
                              Health {b.health_score}
                            </span>
                          </div>
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <Row key={r.testid} {...r} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
